import { useEffect, useRef, useState } from 'react';
import { revealCop30 } from '../state/actions.js';

const STATUS_LINES = [
  'Convening delegates',
  'Auditing emissions ledger',
  'Negotiating carbon penalties',
  'Tallying ratification votes'
];

const RIBBON = [
  'EU',
  'AOSIS',
  'African Group',
  'LDCs',
  'BASIC',
  'Umbrella Group',
  'Arab Group',
  'AILAC'
];

const TIMING = {
  shuttersClose: 600,
  statusFirst: 200,
  statusGap: 1000,
  sealHold: 1100,
  shuttersOpen: 600
};

export function Cop30TransitionScreen({ state, dispatch, onComplete }) {
  const [stage, setStage] = useState('closing');
  const [statusIndex, setStatusIndex] = useState(-1);
  const dispatchedRef = useRef(false);
  const completedRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setStage('processing'), TIMING.shuttersClose);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (stage !== 'processing') return;
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;
    if (!state.cop30) {
      dispatch(revealCop30());
    }
  }, [stage, dispatch, state.cop30]);

  useEffect(() => {
    if (stage !== 'processing') return;
    if (statusIndex < 0) {
      const t = setTimeout(() => setStatusIndex(0), TIMING.statusFirst);
      return () => clearTimeout(t);
    }
    if (statusIndex >= STATUS_LINES.length) return;
    const t = setTimeout(() => {
      const next = statusIndex + 1;
      if (next < STATUS_LINES.length) {
        setStatusIndex(next);
      } else {
        setStatusIndex(STATUS_LINES.length);
        setStage('seal');
      }
    }, TIMING.statusGap);
    return () => clearTimeout(t);
  }, [stage, statusIndex]);

  useEffect(() => {
    if (stage !== 'seal') return;
    const t = setTimeout(() => setStage('opening'), TIMING.sealHold);
    return () => clearTimeout(t);
  }, [stage]);

  useEffect(() => {
    if (stage !== 'opening') return;
    if (completedRef.current) return;
    completedRef.current = true;
    const t = setTimeout(() => {
      setStage('done');
      onComplete?.();
    }, TIMING.shuttersOpen);
    return () => clearTimeout(t);
  }, [stage, onComplete]);

  const ribbonLit =
    statusIndex < 0
      ? 0
      : Math.min(RIBBON.length, (statusIndex + 1) * 2);
  const finalActive =
    stage === 'seal' || stage === 'opening' || stage === 'done';

  return (
    <div
      className="cop-transition"
      data-stage={stage}
      role="status"
      aria-live="polite"
      aria-label="Convening COP 31 Antalya summit"
    >
      <div
        className="cop-transition-shutter cop-transition-shutter-l"
        aria-hidden="true"
      />
      <div
        className="cop-transition-shutter cop-transition-shutter-r"
        aria-hidden="true"
      />
      <div className="cop-transition-seam" aria-hidden="true" />

      <div className="cop-transition-processing">
        <div className="cop-transition-eyebrow">
          {finalActive
            ? 'COP 31 Antalya, accord ratified'
            : 'COP 31 Antalya, convening'}
        </div>

        <div className="cop-transition-globe-wrap">
          <svg
            className="cop-transition-globe"
            viewBox="0 0 100 100"
            aria-hidden="true"
          >
            <circle
              className="cop-transition-globe-core"
              cx="50"
              cy="50"
              r="40"
            />
            <circle
              className="cop-transition-globe-outline"
              cx="50"
              cy="50"
              r="40"
            />
            <ellipse
              className="cop-transition-globe-lat"
              cx="50"
              cy="50"
              rx="40"
              ry="14"
            />
            <ellipse
              className="cop-transition-globe-lat"
              cx="50"
              cy="50"
              rx="40"
              ry="28"
            />
            <line
              className="cop-transition-globe-lat"
              x1="10"
              y1="50"
              x2="90"
              y2="50"
            />
            <ellipse
              className="cop-transition-globe-lon"
              cx="50"
              cy="50"
              rx="14"
              ry="40"
            />
            <ellipse
              className="cop-transition-globe-lon"
              cx="50"
              cy="50"
              rx="28"
              ry="40"
            />
            <circle
              className="cop-transition-globe-pin"
              cx="36"
              cy="32"
              r="2.4"
              style={{ animationDelay: '0s' }}
            />
            <circle
              className="cop-transition-globe-pin"
              cx="62"
              cy="38"
              r="2.4"
              style={{ animationDelay: '0.8s' }}
            />
            <circle
              className="cop-transition-globe-pin"
              cx="44"
              cy="62"
              r="2.4"
              style={{ animationDelay: '1.6s' }}
            />
            <circle
              className="cop-transition-globe-pin"
              cx="68"
              cy="56"
              r="2.4"
              style={{ animationDelay: '2.4s' }}
            />
          </svg>

          <div className="cop-transition-seal" aria-hidden="true">
            <svg viewBox="0 0 100 100">
              <circle
                className="cop-transition-seal-ring"
                cx="50"
                cy="50"
                r="46"
              />
              <circle
                className="cop-transition-seal-ring"
                cx="50"
                cy="50"
                r="38"
              />
              <defs>
                <path
                  id="cop-transition-seal-path"
                  d="M 50 50 m -42 0 a 42 42 0 1 1 84 0 a 42 42 0 1 1 -84 0"
                />
              </defs>
              <text className="cop-transition-seal-text">
                <textPath
                  href="#cop-transition-seal-path"
                  startOffset="0"
                >
                  RATIFIED | COP 31 ACCORD | ANTALYA |
                </textPath>
              </text>
              <path
                className="cop-transition-seal-check"
                d="M32 52 L46 66 L72 36"
              />
              <circle
                className="cop-transition-seal-star"
                cx="50"
                cy="8"
                r="1.6"
              />
              <circle
                className="cop-transition-seal-star"
                cx="92"
                cy="50"
                r="1.6"
              />
              <circle
                className="cop-transition-seal-star"
                cx="50"
                cy="92"
                r="1.6"
              />
              <circle
                className="cop-transition-seal-star"
                cx="8"
                cy="50"
                r="1.6"
              />
            </svg>
          </div>
        </div>

        <div className="cop-transition-status">
          {STATUS_LINES.map((line, i) => {
            const isActive = i === statusIndex && !finalActive;
            return (
              <div
                key={line}
                className={
                  'cop-transition-status-line' +
                  (isActive ? ' cop-transition-status-line-active' : '')
                }
                data-testid={`cop-transition-status-${i}`}
              >
                <span className="cop-transition-dot" />
                <span className="cop-transition-text">{line}</span>
                <span
                  className="cop-transition-ellipsis"
                  aria-hidden="true"
                />
              </div>
            );
          })}
          <div
            className={
              'cop-transition-status-line cop-transition-status-line-final' +
              (finalActive ? ' cop-transition-status-line-active' : '')
            }
            data-testid="cop-transition-status-complete"
          >
            <span className="cop-transition-dot cop-transition-dot-final" />
            <span className="cop-transition-text">Accord ratified</span>
          </div>
        </div>

        <div className="cop-transition-ribbon" aria-hidden="true">
          {RIBBON.map((nation, i) => (
            <span
              key={nation}
              className={
                i < ribbonLit ? 'cop-transition-ribbon-lit' : undefined
              }
            >
              {nation}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
